import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Container,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Card,
  CardContent,
  Chip,
  FormHelperText,
  LinearProgress
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EngineeringIcon from '@mui/icons-material/Engineering';
import CategoryIcon from '@mui/icons-material/Category';
import ApartmentIcon from '@mui/icons-material/Apartment';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

// Helper to sort categories/tasks that use roman numeral + numeric prefixes, e.g. "I.00 Task"
const romanToInt = (roman) => {
  if (!roman) return null;
  const values = { I: 1, V: 5, X: 10, L: 50, C: 100, D: 500, M: 1000 };
  let sum = 0;
  let prev = 0;
  for (let i = roman.length - 1; i >= 0; i -= 1) {
    const val = values[roman[i]] || 0;
    if (val < prev) {
      sum -= val;
    } else {
      sum += val;
    }
    prev = val;
  }
  return sum;
};

const extractPrefix = (name = '') => {
  const match = name.trim().match(/^([IVXLCDM]+)\.?\s*([0-9]+)?/i);
  const romanPart = match?.[1]?.toUpperCase() || null;
  return {
    romanValue: romanPart ? romanToInt(romanPart) : null,
    numberValue: match?.[2] !== undefined ? parseInt(match[2], 10) : null
  };
};

const sortByStandardPrefix = (a, b) => {
  const aParts = extractPrefix(a?.name || '');
  const bParts = extractPrefix(b?.name || '');

  if (aParts.romanValue !== null && bParts.romanValue !== null) {
    if (aParts.romanValue !== bParts.romanValue) {
      return aParts.romanValue - bParts.romanValue;
    }
    if (aParts.numberValue !== null && bParts.numberValue !== null && aParts.numberValue !== bParts.numberValue) {
      return aParts.numberValue - bParts.numberValue;
    }
    if (aParts.numberValue !== null && bParts.numberValue === null) return 1;
    if (aParts.numberValue === null && bParts.numberValue !== null) return -1;
  } else if (aParts.romanValue !== null || bParts.romanValue !== null) {
    return aParts.romanValue !== null ? -1 : 1;
  }

  return (a?.name || '').localeCompare(b?.name || '');
};

const toDateInputValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

const ensureOption = (items, option) => {
  if (!option?.id) return items;
  const exists = items.some(item => String(item.id) === String(option.id));
  if (exists) return items;
  return [...items, option];
};

const formatWeightPercent = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(2) : '0.00';
};

const getTodayDateInput = () => new Date().toISOString().slice(0, 10);

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const formatNumber = (value, digits = 2) => {
  const num = toNumber(value, 0);
  return num.toFixed(digits);
};

const getRemainingVolume = (total, completed) => {
  const remaining = toNumber(total, 0) - toNumber(completed, 0);
  return remaining < 0 ? 0 : remaining;
};

const getProgressPercent = (completed, total) => {
  const totalValue = toNumber(total, 0);
  if (totalValue <= 0) return 0;
  const progress = (toNumber(completed, 0) / totalValue) * 100;
  return Math.min(100, Math.max(0, progress));
};

const getStatusLabel = (completed, total) => {
  const totalValue = toNumber(total, 0);
  const doneValue = toNumber(completed, 0);
  if (totalValue <= 0 || doneValue <= 0) return 'Belum Mulai';
  if (doneValue >= totalValue) return 'Selesai';
  return 'Berjalan';
};

const volumePattern = /^\d*(\.\d{0,2})?$/;
const NOTE_CATEGORY_OPTIONS = [
  { value: 'mos', label: 'MOS' },
  { value: 'pemasangan', label: 'PEMASANGAN' },
  { value: 'mos_pemasangan', label: 'MOS & PEMASANGAN' },
  { value: 'lainnya', label: 'LAINNYA' }
];

const normalizeNoteCategoryValue = (value) => {
  if (value === null || value === undefined) return '';
  const normalized = String(value).trim().toLowerCase();

  if (normalized === 'material') return 'mos';
  if (normalized === 'instalasi') return 'pemasangan';
  if (normalized === 'mos & pemasangan' || normalized === 'mos+pemasangan' || normalized === 'mos/pemasangan') {
    return 'mos_pemasangan';
  }

  return ['mos', 'pemasangan', 'mos_pemasangan', 'lainnya'].includes(normalized) ? normalized : '';
};

const getVolumeError = (value, remainingVolume) => {
  if (!value) return 'Volume harus diisi.';
  if (!volumePattern.test(value)) {
    return 'Volume maksimal 2 angka di belakang koma.';
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return 'Volume harus lebih besar dari 0.';
  }
  if (parsed > remainingVolume) {
    return 'Volume melebihi sisa volume pekerjaan.';
  }
  return '';
};

const normalizeTaskData = (task = {}) => ({
  ...task,
  weight: task.weight !== null && task.weight !== undefined ? Number(task.weight) : task.weight,
  unitPrice: task.unitPrice !== null && task.unitPrice !== undefined ? Number(task.unitPrice) : task.unitPrice,
  quantity: task.quantity !== null && task.quantity !== undefined ? Number(task.quantity) : task.quantity,
  progress: task.progress !== null && task.progress !== undefined ? Number(task.progress) : task.progress,
  completedQuantity: task.completedQuantity !== null && task.completedQuantity !== undefined
    ? Number(task.completedQuantity)
    : task.completedQuantity
});

const UpdateProgress = () => {
  // eslint-disable-next-line no-unused-vars
  const { user } = useAuth();
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();
  const { submissionId } = useParams();
  const isEditing = Boolean(submissionId);
  
  // State for form data
  const [formData, setFormData] = useState({
    projectId: '',
    modularUnitId: '',
    workCategoryId: '',
    taskId: '',
    subconId: '',
    volume: '',
    notes: '',
    noteCategory: '',
    attachments: [],
    // Add progress work date (when the work was actually done)
    progressWorkDate: getTodayDateInput()
  });

  const [existingAttachments, setExistingAttachments] = useState([]);
  const [removedAttachmentIds, setRemovedAttachmentIds] = useState([]);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [submissionRejectionReason, setSubmissionRejectionReason] = useState('');
  const [loadingSubmission, setLoadingSubmission] = useState(false);
  const [isPrefilling, setIsPrefilling] = useState(false);

  const getAttachmentIcon = (fileType) => {
    if (fileType?.startsWith('image/')) {
      return <ImageIcon fontSize="small" />;
    }
    if (fileType === 'application/pdf') {
      return <PictureAsPdfIcon fontSize="small" />;
    }
    return <InsertDriveFileIcon fontSize="small" />;
  };

  const getAttachmentUrl = (fileUrl) => {
    if (!fileUrl) return '';
    if (/^https?:\/\//i.test(fileUrl)) return fileUrl;
    const base = api.defaults.baseURL || '';
    const baseRoot = base.replace(/\/api\/?$/, '');
    if (fileUrl.startsWith('/')) return `${baseRoot}${fileUrl}`;
    return `${baseRoot}/uploads/${fileUrl.replace(/^\/+/, '')}`;
  };
  
  // State for loading and error handling
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const isReadOnly =
    isEditing &&
    submissionStatus &&
    !['draft', 'rejected'].includes(submissionStatus);
  const isBusy = loading || submitting || loadingSubmission;
  
  // State for dropdown options
  const [projects, setProjects] = useState([]);
  const [modularUnits, setModularUnits] = useState([]);
  const [workCategories, setWorkCategories] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [installationTeams, setInstallationTeams] = useState([]);
  const [installationTeamsLoading, setInstallationTeamsLoading] = useState(false);
  const [installationTeamsError, setInstallationTeamsError] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  
  // State for stepper
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Pilih Proyek', 'Pilih Unit Bangunan & Kategori Pekerjaan', 'Pilih Tugas', 'Input Progress'];

  const totalVolume = toNumber(selectedTask?.quantity, 0);
  const completedVolume = toNumber(selectedTask?.completedQuantity, 0);
  const remainingVolume = getRemainingVolume(totalVolume, completedVolume);
  const volumeValue = toNumber(formData.volume, 0);
  const projectedCompleted = completedVolume + volumeValue;
  const currentProgress = getProgressPercent(completedVolume, totalVolume);
  const projectedProgress = getProgressPercent(projectedCompleted, totalVolume);
  const volumeError =
    formData.volume !== '' ? getVolumeError(formData.volume, remainingVolume) : '';
  const isTaskDone = totalVolume > 0 && remainingVolume <= 0;
  const isVolumeValid = !volumeError && formData.volume !== '';
  const isNotesRequired = formData.noteCategory === 'lainnya';
  const isNotesValid = !isNotesRequired || Boolean(formData.notes && formData.notes.trim());
  const canSubmit = Boolean(
    formData.projectId &&
      formData.taskId &&
      formData.subconId &&
      formData.progressWorkDate &&
      formData.noteCategory &&
      isNotesValid &&
      isVolumeValid &&
      !isBusy &&
      !isReadOnly &&
      !isTaskDone &&
      totalVolume > 0
  );

  const sortedWorkCategories = useMemo(
    () => [...workCategories].sort(sortByStandardPrefix),
    [workCategories]
  );

  const sortedTasks = useMemo(
    () => [...tasks].sort(sortByStandardPrefix),
    [tasks]
  );
  
  // Fetch projects on component mount
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        console.log('Fetching projects for user role:', user?.role);
        
        // Always use the user's projects endpoint to ensure proper filtering
        // This will only return projects the user has access to
        const response = await api.get('/users/me/projects');
        console.log('Using /users/me/projects endpoint to get assigned projects');
        
        // Check if we got the rows property (paginated response) or just an array
        const projectData = response.data.rows || response.data;
        
        // Filter out archived projects
        const activeProjects = projectData.filter(project => !project.isArchived);
        
        if (activeProjects && activeProjects.length > 0) {
          setProjects(activeProjects);
          console.log('Loaded assigned projects:', activeProjects.length);
        } else {
          console.log('No projects found for this user');
          setError('No projects assigned to you. Please contact your administrator.');
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching projects:', err);
        setError('Failed to load projects. Please try again.');
        setLoading(false);
      }
    };
    
    fetchProjects();
  }, [user?.role]);

  useEffect(() => {
    if (!isEditing) return;

    const fetchSubmission = async () => {
      try {
        setLoadingSubmission(true);
        setIsPrefilling(true);

        const response = await api.get(`/progress-submissions/${submissionId}`);
        const submission = response.data || {};

        setFormData(prev => ({
          ...prev,
          projectId: submission.projectId || '',
          modularUnitId: submission.modularUnitId || '',
          workCategoryId: submission.workCategoryId || '',
          taskId: submission.taskId || '',
          subconId: submission.subconId || '',
          volume: submission.volume ?? '',
          notes: submission.notes ?? '',
          noteCategory: normalizeNoteCategoryValue(submission.noteCategory),
          attachments: [],
          progressWorkDate: toDateInputValue(submission.progressWorkDate) || getTodayDateInput()
        }));

        if (submission.projectId) {
          setProjects(prev =>
            ensureOption(prev, {
              id: submission.projectId,
              name: submission.projectName || `Project ${submission.projectId}`
            })
          );
        }

        if (submission.modularUnitId) {
          setModularUnits(prev =>
            ensureOption(prev, {
              id: submission.modularUnitId,
              name: submission.modularUnitName || `Unit ${submission.modularUnitId}`
            })
          );
        }

        if (submission.workCategoryId) {
          setWorkCategories(prev =>
            ensureOption(prev, {
              id: submission.workCategoryId,
              name: submission.workCategoryName || `Kategori ${submission.workCategoryId}`
            })
          );
        }

        if (submission.taskId) {
          setTasks(prev =>
            ensureOption(prev, {
              id: submission.taskId,
              name: submission.taskName || `Task ${submission.taskId}`
            })
          );
          setSelectedTask(normalizeTaskData({
            id: submission.taskId,
            name: submission.taskName,
            quantity: submission.taskQuantity,
            unit: submission.taskUnit,
            completedQuantity: submission.taskCompletedQuantity,
            progress: submission.taskProgress,
            status: submission.taskStatus
          }));
        }

        if (submission.subconId) {
          setInstallationTeams(prev =>
            ensureOption(prev, {
              id: submission.subconId,
              name: submission.subconName || `Subcon ${submission.subconId}`
            })
          );
        }

        setSubmissionStatus(submission.status || null);
        setSubmissionRejectionReason(submission.rejectionReason || '');
        setExistingAttachments(Array.isArray(submission.attachments) ? submission.attachments : []);
        setActiveStep(3);
      } catch (err) {
        console.error('Error fetching submission:', err);
        setError(err.response?.data?.message || 'Failed to load progress submission. Please try again.');
      } finally {
        setLoadingSubmission(false);
        setIsPrefilling(false);
      }
    };

    fetchSubmission();
  }, [isEditing, submissionId]);
  
  useEffect(() => {
    const fetchInstallationTeams = async () => {
      try {
        setInstallationTeamsLoading(true);
        setInstallationTeamsError(null);
        
        const response = await api.get('/installation-teams');
        const payload = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.installationTeams)
          ? response.data.installationTeams
          : Array.isArray(response.data?.data)
          ? response.data.data
          : Array.isArray(response.data?.items)
          ? response.data.items
          : Array.isArray(response.data?.result)
          ? response.data.result
          : [];
        
        const activeTeams = payload.filter(team => team?.isActive !== false);
        setInstallationTeams(activeTeams);
      } catch (err) {
        console.error('Error fetching installation teams:', err);
        setInstallationTeams([]);
        setInstallationTeamsError(err.response?.data?.message || 'Gagal memuat daftar subcon. Silakan coba lagi atau hubungi administrator.');
      } finally {
        setInstallationTeamsLoading(false);
      }
    };
    
    fetchInstallationTeams();
  }, []);
  
  // Fetch modular units when project is selected
  useEffect(() => {
    if (formData.projectId) {
      const fetchModularUnits = async () => {
        try {
          setLoading(true);
          setError(null); // Clear any previous errors
          
          console.log(`Fetching modular units for project ${formData.projectId}`);
          const response = await api.get(`/projects/${formData.projectId}/modular-units`);
          
          // Backend returns { modularUnits: [...] } format
          if (response.data && response.data.modularUnits && Array.isArray(response.data.modularUnits)) {
            setModularUnits(response.data.modularUnits);
            console.log(`Loaded ${response.data.modularUnits.length} modular units for project ${formData.projectId}`);
            
            if (response.data.modularUnits.length === 0) {
              setError('No modular units found for this project. Please contact your administrator.');
            }
          } else {
            console.error('Invalid response format for modular units:', response.data);
            setError('Failed to load modular units. Invalid data format.');
            setModularUnits([]);
          }
          
          // Reset dependent fields only when creating new submission
          if (!isEditing && !isPrefilling) {
            setFormData(prev => ({
              ...prev,
              modularUnitId: '',
              workCategoryId: '',
              taskId: ''
            }));
            setWorkCategories([]);
            setTasks([]);
            setSelectedTask(null);
          }
          setLoading(false);
        } catch (err) {
          console.error('Error fetching modular units:', err);
          setError(err.response?.data?.message || 'Failed to load modular units. Please try again.');
          setModularUnits([]);
          setLoading(false);
        }
      };
      
      fetchModularUnits();
    }
  }, [formData.projectId, isEditing, isPrefilling]);
  
  // Fetch work categories when modular unit is selected
  useEffect(() => {
    if (formData.modularUnitId) {
      const fetchWorkCategories = async () => {
        try {
          setLoading(true);
          setError(null); // Clear any previous errors
          
          console.log(`Fetching work categories for modular unit ${formData.modularUnitId}`);
          const response = await api.get(`/modular-units/${formData.modularUnitId}/work-categories`);
          
          // Backend returns { workCategories: [...] } format
          if (response.data && response.data.workCategories && Array.isArray(response.data.workCategories)) {
            setWorkCategories(response.data.workCategories);
            console.log(`Loaded ${response.data.workCategories.length} work categories for modular unit ${formData.modularUnitId}`);
            
            if (response.data.workCategories.length === 0) {
              setError('No work categories found for this modular unit. Please contact your administrator.');
            }
          } else {
            console.error('Invalid response format for work categories:', response.data);
            setError('Failed to load work categories. Invalid data format.');
            setWorkCategories([]);
          }
          
          // Reset dependent fields only when creating new submission
          if (!isEditing && !isPrefilling) {
            setFormData(prev => ({
              ...prev,
              workCategoryId: '',
              taskId: ''
            }));
            setTasks([]);
            setSelectedTask(null);
          }
          setLoading(false);
        } catch (err) {
          console.error('Error fetching work categories:', err);
          setError(err.response?.data?.message || 'Failed to load work categories. Please try again.');
          setWorkCategories([]);
          setLoading(false);
        }
      };
      
      fetchWorkCategories();
    }
  }, [formData.modularUnitId, isEditing, isPrefilling]);
  
  // Fetch tasks when work category is selected
  useEffect(() => {
    if (formData.workCategoryId) {
      const fetchTasks = async () => {
        try {
          setLoading(true);
          setError(null); // Clear any previous errors
          
          console.log(`Fetching tasks for work category ${formData.workCategoryId}`);
          const response = await api.get(`/work-categories/${formData.workCategoryId}/tasks`);
          
          // Backend returns { tasks: [...] } format
          if (response.data && response.data.tasks && Array.isArray(response.data.tasks)) {
            setTasks(response.data.tasks);
            console.log(`Loaded ${response.data.tasks.length} tasks for work category ${formData.workCategoryId}`);
            
            if (response.data.tasks.length === 0) {
              setError(
                'Belum ada tugas yang tersedia untuk kategori pekerjaan ini. ' +
                'Anda perlu menambahkan tugas terlebih dahulu di halaman Gantt Chart ' +
                'atau hubungi Project Manager untuk menambahkan tugas pada kategori ini.'
              );
            }
          } else {
            console.error('Invalid response format for tasks:', response.data);
            setError('Failed to load tasks. Invalid data format.');
            setTasks([]);
          }
          
          // Reset dependent fields only when creating new submission
          if (!isEditing && !isPrefilling) {
            setFormData(prev => ({
              ...prev,
              taskId: ''
            }));
            setSelectedTask(null);
          }
          setLoading(false);
        } catch (err) {
          console.error('Error fetching tasks:', err);
          setError(err.response?.data?.message || 'Failed to load tasks. Please try again.');
          setTasks([]);
          setLoading(false);
        }
      };
      
      fetchTasks();
    }
  }, [formData.workCategoryId, isEditing, isPrefilling]);
  
  // Fetch task details when task is selected
  useEffect(() => {
    if (formData.taskId && formData.projectId) {
      const fetchTaskDetails = async () => {
        try {
          setLoading(true);
          
          // Use project-specific task endpoint to get BOQ data (quantity, unitPrice, weight)
          const response = await api.get(`/projects/${formData.projectId}/tasks/${formData.taskId}`);
          
          console.log('Task details response:', response.data);
          console.log('BOQ Data check:', {
            quantity: response.data.quantity,
            unitPrice: response.data.unitPrice,
            weight: response.data.weight,
            autoCalculateWeight: response.data.autoCalculateWeight
          });
          
          // Check if response.data is the task object directly
          if (response.data && response.data.id) {
            const taskData = response.data;
            
            // Calculate formattedTotalCost if unitPrice and quantity are available
            if (taskData.unitPrice && taskData.quantity) {
              const totalCost = parseFloat(taskData.unitPrice) * parseFloat(taskData.quantity);
              taskData.formattedTotalCost = new Intl.NumberFormat('id-ID', { 
                style: 'currency', 
                currency: 'IDR' 
              }).format(totalCost);
            }
            
            console.log('Setting selectedTask with data:', taskData);
            console.log('BOQ values:', {
              unitPrice: taskData.unitPrice,
              quantity: taskData.quantity,
              weight: taskData.weight,
              autoCalculateWeight: taskData.autoCalculateWeight
            });
            setSelectedTask(normalizeTaskData(taskData));
          } 
          // Check if response.data has a task property (old format)
          else if (response.data && response.data.task && response.data.task.id) {
            const taskData = response.data.task;
            
            // Calculate formattedTotalCost if unitPrice and quantity are available
            if (taskData.unitPrice && taskData.quantity) {
              const totalCost = parseFloat(taskData.unitPrice) * parseFloat(taskData.quantity);
              taskData.formattedTotalCost = new Intl.NumberFormat('id-ID', { 
                style: 'currency', 
                currency: 'IDR' 
              }).format(totalCost);
            }
            
            setSelectedTask(normalizeTaskData(taskData));
          } else {
            console.error('Invalid task data format:', response.data);
            setError('Failed to load task details. Invalid data format.');
          }
          
          setLoading(false);
        } catch (err) {
          console.error('Error fetching task details:', err);
          setError('Failed to load task details. Please try again.');
          setLoading(false);
        }
      };
      
      fetchTaskDetails();
    }
  }, [formData.taskId, formData.projectId]);
  
  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    const normalizedValue = name === 'noteCategory' ? normalizeNoteCategoryValue(value) : value;

    if (name === 'volume' && value && !volumePattern.test(value)) {
      return;
    }

    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: normalizedValue
      };

      if (name === 'noteCategory' && normalizedValue !== 'lainnya') {
        newData.notes = '';
      }

      return newData;
    });
    
    // Update stepper based on selection
    if (name === 'projectId' && value) {
      setActiveStep(1);
    } else if ((name === 'modularUnitId' || name === 'workCategoryId') && value) {
      setActiveStep(2);
    } else if (name === 'taskId' && value) {
      setActiveStep(3);
    }
  };
  
  // Handle file upload
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    
    // Batasi jumlah file (maksimal 5 termasuk lampiran lama)
    const existingCount = existingAttachments.length;
    if (existingCount + formData.attachments.length + files.length > 5) {
      setError('Maksimal 5 file yang diperbolehkan');
      return;
    }
    
    // Batasi ukuran file (maksimal 5MB per file)
    const oversizedFiles = files.filter(file => file.size > 5 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      setError('Ukuran file tidak boleh melebihi 5MB');
      return;
    }
    
    // Tambahkan preview URL untuk file gambar
    const filesWithPreview = files.map(file => {
      const isImage = file.type.startsWith('image/');
      return {
        file,
        preview: isImage ? URL.createObjectURL(file) : null,
        type: file.type
      };
    });
    
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...filesWithPreview]
    }));
  };
  
  // Tambahkan fungsi untuk menghapus file
  const handleRemoveFile = (index) => {
    const newAttachments = [...formData.attachments];
    
    // Hapus object URL untuk mencegah memory leak
    if (newAttachments[index].preview) {
      URL.revokeObjectURL(newAttachments[index].preview);
    }
    
    newAttachments.splice(index, 1);
    
    setFormData(prev => ({
      ...prev,
      attachments: newAttachments
    }));
  };

  const handleRemoveExistingAttachment = (attachmentId) => {
    setExistingAttachments(prev => prev.filter(att => att.id !== attachmentId));
    setRemovedAttachmentIds(prev => [...prev, attachmentId]);
  };
  
  // Tambahkan useEffect untuk cleanup object URLs saat component unmount
  useEffect(() => {
    return () => {
      formData.attachments.forEach(attachment => {
        if (attachment.preview) {
          URL.revokeObjectURL(attachment.preview);
        }
      });
    };
  }, [formData.attachments]);
  
  // Modifikasi handleSubmit untuk menangani multiple files
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isReadOnly) {
      setError('Pengajuan ini sudah disubmit/disetujui dan tidak bisa diedit.');
      return;
    }

    if (!formData.projectId || !formData.taskId || !formData.subconId) {
      setError('Lengkapi data wajib: proyek, tugas, dan subcon.');
      return;
    }

    if (!formData.noteCategory) {
      setError('Kategori keterangan wajib dipilih.');
      return;
    }
    if (formData.noteCategory === 'lainnya' && !formData.notes.trim()) {
      setError('Keterangan tambahan wajib diisi jika kategori Lainnya dipilih.');
      return;
    }

    if (!formData.progressWorkDate) {
      setError('Tanggal pengerjaan wajib diisi.');
      return;
    }

    if (!selectedTask) {
      setError('Detail tugas belum siap. Silakan tunggu sebentar.');
      return;
    }

    const totalVolume = toNumber(selectedTask?.quantity, 0);
    const completedVolume = toNumber(selectedTask?.completedQuantity, 0);
    const remainingVolume = getRemainingVolume(totalVolume, completedVolume);

    if (totalVolume <= 0) {
      setError('Total volume pekerjaan belum ditentukan.');
      return;
    }

    if (remainingVolume <= 0) {
      setError('Task sudah selesai. Progress tidak dapat ditambahkan.');
      return;
    }

    const volumeError = getVolumeError(formData.volume, remainingVolume);
    if (volumeError) {
      setError(volumeError);
      return;
    }
    
    try {
      setSubmitting(true);
      
      // Create form data object for file upload
      const submitData = new FormData();
      submitData.append('projectId', formData.projectId);
      submitData.append('taskId', formData.taskId);
      submitData.append('subconId', formData.subconId);
      submitData.append('volume', formData.volume);
      submitData.append('noteCategory', formData.noteCategory);
      
      if (formData.noteCategory === 'lainnya' && formData.notes) {
        submitData.append('notes', formData.notes);
      }
      
      // Add progress work date (when the work was actually done)
      if (formData.progressWorkDate) {
        submitData.append('progressWorkDate', formData.progressWorkDate);
      }
      
      // Handle multiple file uploads
      if (formData.attachments && formData.attachments.length > 0) {
        formData.attachments.forEach(attachment => {
          submitData.append('attachments', attachment.file);
        });
      }

      if (isEditing && removedAttachmentIds.length > 0) {
        submitData.append('deletedAttachmentIds', JSON.stringify(removedAttachmentIds));
      }
      
      // Submit progress
      if (isEditing) {
        await api.put(`/progress-submissions/${submissionId}`, submitData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        await api.post(`/progress-submissions/${submissionId}/submit`);

        setSuccess('Progress berhasil dikirim ulang. Menunggu persetujuan.');
        setSubmitting(false);
        navigate('/progress/submissions');
        return;
      }

      await api.post('/progress-submissions', submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setSuccess('Progress submitted successfully! Waiting for approval.');
      
      // Reset form
      setFormData({
        projectId: formData.projectId,
        modularUnitId: formData.modularUnitId,
        workCategoryId: formData.workCategoryId,
        taskId: '',
        subconId: formData.subconId,
        volume: '',
        notes: '',
        noteCategory: '',
        attachments: [],
        progressWorkDate: getTodayDateInput()
      });
      
      setSelectedTask(null);
      
      // Reset file input
      const fileInput = document.getElementById('attachment-input');
      if (fileInput) {
        fileInput.value = '';
      }
      
      setSubmitting(false);
    } catch (err) {
      console.error('Error submitting progress:', err);
      setError(err.response?.data?.message || 'Failed to submit progress. Please try again.');
      setSubmitting(false);
    }
  };
  
  // Handle snackbar close
  const handleSnackbarClose = () => {
    setError(null);
    setSuccess(null);
  };
  
  return (
    <Container maxWidth="lg" sx={{ textTransform: 'uppercase' }}>
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', color: '#3f51b5', textAlign: 'center' }}>
          {isEditing ? 'Edit Pembaharuan Kemajuan' : 'Project Progress Update'}
        </Typography>

        {isReadOnly && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            Pengajuan ini sudah dikirim atau disetujui sehingga tidak dapat diedit.
          </Alert>
        )}

        {isEditing && ['draft', 'rejected'].includes(submissionStatus) && submissionRejectionReason && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Alasan Reject: {submissionRejectionReason}
          </Alert>
        )}

        {loadingSubmission && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
            <CircularProgress />
          </Box>
        )}
        
        {/* BOQ Summary Card - Shown when task is selected */}
        {selectedTask && (
          <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 3, bgcolor: 'background.paper' }}>
            {/* Header Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}>
                📋 Ringkasan BOQ
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'medium', color: 'text.primary', mb: 1 }}>
                {selectedTask.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                {selectedTask.quantity && selectedTask.unit && (
                  <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 'medium' }}>
                    📏 Volume: <strong>{selectedTask.quantity} {selectedTask.unit}</strong>
                  </Typography>
                )}
                {selectedTask.jobCode && (
                  <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 'medium' }}>
                    🔢 Kode: <strong>{selectedTask.jobCode}</strong>
                  </Typography>
                )}
              </Box>
            </Box>

            {/* BOQ Information Cards */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: 2, 
                    textAlign: 'center', 
                    bgcolor: (selectedTask.unitPrice && selectedTask.unitPrice > 0) ? 'success.light' : 'grey.100',
                    border: (selectedTask.unitPrice && selectedTask.unitPrice > 0) ? '2px solid' : '1px solid',
                    borderColor: (selectedTask.unitPrice && selectedTask.unitPrice > 0) ? 'success.main' : 'grey.300',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    minHeight: '80px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 1 }}>
                    💰 Harga Satuan
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 'bold', 
                      color: (selectedTask.unitPrice && selectedTask.unitPrice > 0) ? 'success.dark' : 'text.disabled',
                      mb: 0.5,
                      fontSize: '1.1rem'
                    }}
                  >
                    {(selectedTask.unitPrice && selectedTask.unitPrice > 0) ? 
                      new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(selectedTask.unitPrice) : 
                      '---'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {(selectedTask.unitPrice && selectedTask.unitPrice > 0) ? 
                      `per ${selectedTask.unit || 'unit'}` : 
                      'Belum diisi'}
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: 2, 
                    textAlign: 'center', 
                    bgcolor: (selectedTask.unitPrice && selectedTask.unitPrice > 0 && selectedTask.quantity && selectedTask.quantity > 0) ? 'info.light' : 'grey.100',
                    border: (selectedTask.unitPrice && selectedTask.unitPrice > 0 && selectedTask.quantity && selectedTask.quantity > 0) ? '2px solid' : '1px solid',
                    borderColor: (selectedTask.unitPrice && selectedTask.unitPrice > 0 && selectedTask.quantity && selectedTask.quantity > 0) ? 'info.main' : 'grey.300',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    minHeight: '80px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 1 }}>
                    💵 Total Biaya
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 'bold', 
                      color: (selectedTask.unitPrice && selectedTask.unitPrice > 0 && selectedTask.quantity && selectedTask.quantity > 0) ? 'info.dark' : 'text.disabled',
                      mb: 0.5,
                      fontSize: '1.1rem'
                    }}
                  >
                    {(selectedTask.unitPrice && selectedTask.unitPrice > 0 && selectedTask.quantity && selectedTask.quantity > 0) ? 
                      new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(selectedTask.unitPrice * selectedTask.quantity) : 
                      '---'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {(selectedTask.unitPrice && selectedTask.unitPrice > 0 && selectedTask.quantity && selectedTask.quantity > 0) ? 
                      `${selectedTask.quantity} ${selectedTask.unit || 'unit'}` : 
                      'Belum diisi'}
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Paper 
                  elevation={2} 
                  sx={{ 
                    p: 2, 
                    textAlign: 'center', 
                    bgcolor: (selectedTask.weight && selectedTask.weight > 0) ? 'warning.light' : 'grey.100',
                    border: (selectedTask.weight && selectedTask.weight > 0) ? '2px solid' : '1px solid',
                    borderColor: (selectedTask.weight && selectedTask.weight > 0) ? 'warning.main' : 'grey.300',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    minHeight: '80px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 1 }}>
                    ⚖️ Bobot Pekerjaan
                  </Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontWeight: 'bold', 
                      color: (selectedTask.weight && selectedTask.weight > 0) ? 'warning.dark' : 'text.disabled',
                      mb: 0.5,
                      fontSize: '1.1rem'
                    }}
                  >
                    {(selectedTask.weight && Number(selectedTask.weight) > 0) ? 
                      `${formatWeightPercent(selectedTask.weight)}%` : 
                      '0.00%'}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {selectedTask.autoCalculateWeight ? 
                      '🤖 Auto Calculate' : 
                      ((selectedTask.weight && Number(selectedTask.weight) > 0) ? 'Manual' : 'Belum diisi')}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>

            {/* Status Summary */}
            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Grid container spacing={1.5} alignItems="center">
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    📊 Progress
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {formatNumber(currentProgress)}%
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    ✅ Selesai
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    {formatNumber(completedVolume)} {selectedTask.unit}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                    🏗️ Status
                  </Typography>
                  <Chip 
                    label={getStatusLabel(completedVolume, totalVolume)} 
                    size="small"
                    color={currentProgress >= 100 ? 'success' : currentProgress > 0 ? 'warning' : 'default'}
                    sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                    👷 Tim
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'medium' }}>
                    {selectedTask.subcontractor?.name || 'Belum ditentukan'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        )}
        
        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        <Paper sx={{ p: 4, mb: 4, borderRadius: 2, boxShadow: 3 }}>
          <form onSubmit={handleSubmit}>
            {/* Step 1: Project Selection */}
            <Card sx={{ mb: 3, display: activeStep >= 1 ? 'block' : 'block', boxShadow: activeStep === 0 ? 3 : 1 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ApartmentIcon sx={{ mr: 1, color: '#3f51b5' }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Pilih Proyek
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />
                
                <FormControl fullWidth required sx={{ minHeight: '56px' }}>
                  <InputLabel 
                    sx={{ 
                      fontSize: '1rem',
                      backgroundColor: 'white',
                      px: 1,
                      '&.Mui-focused': {
                        color: '#3f51b5'
                      }
                    }}
                  >
                    Pilih Proyek
                  </InputLabel>
                  <Select
                    name="projectId"
                    value={formData.projectId}
                    onChange={handleChange}
                      disabled={isBusy || isEditing}
                    sx={{ 
                      minHeight: '56px',
                      '& .MuiSelect-select': {
                        padding: '16px 14px',
                        fontSize: '1rem'
                      },
                      '& .MuiOutlinedInput-root': {
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#3f51b5',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#3f51b5',
                        }
                      }
                    }}
                  >
                    {projects.map(project => (
                      <MenuItem key={project.id} value={project.id} sx={{ fontSize: '1rem', py: 1.5 }}>
                        {project.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </CardContent>
            </Card>
            
            {/* Step 2: Modular Unit & Work Category */}
            <Card sx={{ mb: 3, display: activeStep >= 1 ? 'block' : 'none', boxShadow: activeStep === 1 ? 3 : 1 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <CategoryIcon sx={{ mr: 1, color: '#3f51b5' }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Pilih Unit Bangunan & Kategori Pekerjaan
                  </Typography>
                </Box>
                <Divider sx={{ mb: 4 }} />
                
                <Grid container spacing={3}>
                  {/* Modular Unit Selection */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#3f51b5' }}>
                        Pilih unit bangunan yang akan dikerjakan
                      </Typography>
                    </Box>
                    <FormControl fullWidth required disabled={!formData.projectId || isBusy || isEditing} sx={{ minHeight: '56px' }}>
                      <InputLabel 
                        sx={{ 
                          fontSize: '1rem',
                          backgroundColor: 'white',
                          px: 1,
                          '&.Mui-focused': {
                            color: '#3f51b5'
                          }
                        }}
                      >
                        Pilih Unit Bangunan
                      </InputLabel>
                      <Select
                        name="modularUnitId"
                        value={formData.modularUnitId}
                        onChange={handleChange}
                        sx={{ 
                          minHeight: '56px',
                          '& .MuiSelect-select': {
                            padding: '16px 14px',
                            fontSize: '1rem'
                          },
                          '& .MuiOutlinedInput-root': {
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#3f51b5',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#3f51b5',
                            }
                          }
                        }}
                      >
                        {modularUnits.map(unit => (
                          <MenuItem key={unit.id} value={unit.id} sx={{ fontSize: '1rem', py: 1.5 }}>
                            {unit.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  {/* Work Category Selection */}
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1, color: '#3f51b5' }}>
                        Pilih kategori pekerjaan yang sesuai
                      </Typography>
                    </Box>
                    <FormControl fullWidth required disabled={!formData.modularUnitId || isBusy || isEditing} sx={{ minHeight: '56px' }}>
                      <InputLabel 
                        sx={{ 
                          fontSize: '1rem',
                          backgroundColor: 'white',
                          px: 1,
                          '&.Mui-focused': {
                            color: '#3f51b5'
                          }
                        }}
                      >
                        Pilih Kategori Pekerjaan
                      </InputLabel>
                      <Select
                        name="workCategoryId"
                        value={formData.workCategoryId}
                        onChange={handleChange}
                        sx={{ 
                          minHeight: '56px',
                          '& .MuiSelect-select': {
                            padding: '16px 14px',
                            fontSize: '1rem'
                          },
                          '& .MuiOutlinedInput-root': {
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#3f51b5',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#3f51b5',
                            }
                          }
                        }}
                      >
                        {sortedWorkCategories.map(category => (
                          <MenuItem key={category.id} value={category.id} sx={{ fontSize: '1rem', py: 1.5 }}>
                            {category.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            
            {/* Step 3: Task Selection */}
            <Card sx={{ mb: 3, display: activeStep >= 2 ? 'block' : 'none', boxShadow: activeStep === 2 ? 3 : 1 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AssignmentIcon sx={{ mr: 1, color: '#3f51b5' }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Pilih Tugas
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />
                
                <FormControl fullWidth required disabled={!formData.workCategoryId || isBusy || isEditing} sx={{ minHeight: '56px' }}>
                  <InputLabel 
                    sx={{ 
                      fontSize: '1rem',
                      backgroundColor: 'white',
                      px: 1,
                      '&.Mui-focused': {
                        color: '#3f51b5'
                      }
                    }}
                  >
                    Pilih Tugas
                  </InputLabel>
                  <Select
                    name="taskId"
                    value={formData.taskId}
                    onChange={handleChange}
                    sx={{ 
                      minHeight: '56px',
                      '& .MuiSelect-select': {
                        padding: '16px 14px',
                        fontSize: '1rem'
                      },
                      '& .MuiOutlinedInput-root': {
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#3f51b5',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#3f51b5',
                        }
                      }
                    }}
                  >
                    {sortedTasks.map(task => (
                      <MenuItem key={task.id} value={task.id} sx={{ fontSize: '1rem', py: 1.5 }}>
                        {task.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                {/* Task Details (if selected) */}
                {selectedTask && (
                  <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Detail Tugas:
                    </Typography>
                    <Grid container spacing={2}>
                      {/* Baris pertama - Informasi Volume */}
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2">
                          <strong>Total Volume:</strong> {formatNumber(totalVolume)} {selectedTask.unit}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2">
                          <strong>Volume Selesai:</strong> {formatNumber(completedVolume)} {selectedTask.unit}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2">
                          <strong>Progress Saat Ini:</strong> {formatNumber(currentProgress)}%
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2">
                          <strong>Sisa Volume:</strong> {formatNumber(remainingVolume)} {selectedTask.unit}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <Box sx={{ mt: 0.5 }}>
                          <LinearProgress
                            variant="determinate"
                            value={currentProgress}
                            sx={{ height: 10, borderRadius: 8 }}
                          />
                        </Box>
                      </Grid>
                      
                      {/* Baris kedua - Informasi BOQ */}
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: 'primary.main' }}>
                          Informasi BOQ:
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2">
                          <strong>Harga Satuan:</strong> {selectedTask.unitPrice !== null && selectedTask.unitPrice !== undefined ? 
                            new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(selectedTask.unitPrice) : 
                            'Rp 0,00'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2">
                          <strong>Total Cost:</strong> {selectedTask.unitPrice && selectedTask.quantity ? 
                            new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR' }).format(selectedTask.quantity * selectedTask.unitPrice) : 
                            'Rp 0,00'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography variant="body2">
                          <strong>Bobot Pekerjaan:</strong> {selectedTask.weight !== null && selectedTask.weight !== undefined ? 
                            formatWeightPercent(selectedTask.weight) : '0.00'}%
                          {selectedTask.autoCalculateWeight && 
                            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'primary.main' }}>
                              (Auto)
                            </Typography>
                          }
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </CardContent>
            </Card>
            
            {/* Step 4: Input Progress */}
            <Card sx={{ mb: 3, display: activeStep >= 3 ? 'block' : 'none', boxShadow: activeStep === 3 ? 3 : 1 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <EngineeringIcon sx={{ mr: 1, color: '#3f51b5' }} />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Input Progress Pekerjaan
                  </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        p: 2.5,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: 'rgba(63, 81, 181, 0.04)'
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                        Ringkasan Progress
                      </Typography>
                      {selectedTask ? (
                        <>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Status saat ini: <strong>{getStatusLabel(completedVolume, totalVolume)}</strong>
                          </Typography>
                          <Box sx={{ mt: 1.5 }}>
                            <LinearProgress
                              variant="determinate"
                              value={currentProgress}
                              sx={{ height: 10, borderRadius: 8 }}
                            />
                            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                Saat ini
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                {formatNumber(currentProgress)}%
                              </Typography>
                            </Box>
                          </Box>
                          <Grid container spacing={1.5} sx={{ mt: 1 }}>
                            <Grid item xs={6}>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                Total Volume
                              </Typography>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {formatNumber(totalVolume)} {selectedTask.unit}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                Sisa
                              </Typography>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {formatNumber(remainingVolume)} {selectedTask.unit}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                Selesai
                              </Typography>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {formatNumber(completedVolume)} {selectedTask.unit}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                Estimasi Setelah Update
                              </Typography>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {formatNumber(projectedProgress)}%
                              </Typography>
                            </Grid>
                          </Grid>
                          <Box sx={{ mt: 2 }}>
                            <LinearProgress
                              variant="determinate"
                              value={projectedProgress}
                              sx={{ height: 10, borderRadius: 8, bgcolor: 'rgba(0,0,0,0.04)' }}
                            />
                          </Box>
                        </>
                      ) : (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          Pilih tugas untuk melihat ringkasan progress.
                        </Typography>
                      )}
                    </Box>

                    {selectedTask && totalVolume <= 0 && (
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        Total volume pekerjaan belum ditentukan. Update progress tidak bisa dilakukan.
                      </Alert>
                    )}

                    {selectedTask && isTaskDone && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Task sudah selesai. Progress tidak dapat ditambahkan.
                      </Alert>
                    )}
                  </Grid>

                  <Grid item xs={12} md={8}>
                    {installationTeamsError && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        {installationTeamsError}
                      </Alert>
                    )}

                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required
                          label="Tanggal Pekerjaan Dikerjakan"
                          name="progressWorkDate"
                          type="date"
                          value={formData.progressWorkDate}
                          onChange={handleChange}
                          disabled={!formData.taskId || isBusy || isReadOnly}
                          error={!formData.progressWorkDate && Boolean(formData.taskId)}
                          InputLabelProps={{
                            shrink: true,
                            sx: {
                              fontSize: '1rem',
                              backgroundColor: 'white',
                              px: 1,
                              '&.Mui-focused': {
                                color: '#3f51b5'
                              }
                            }
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#3f51b5',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#3f51b5',
                              }
                            }
                          }}
                          helperText="Wajib diisi. Boleh backdate sesuai tanggal pengerjaan."
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FormControl
                          fullWidth
                          required
                          disabled={installationTeamsLoading || isBusy || installationTeams.length === 0 || isReadOnly}
                        >
                          <InputLabel
                            id="subcon-select-label"
                            sx={{
                              fontSize: '1rem',
                              backgroundColor: 'white',
                              px: 1,
                              '&.Mui-focused': {
                                color: '#3f51b5'
                              }
                            }}
                          >
                            Subcon yang Mengerjakan
                          </InputLabel>
                          <Select
                            labelId="subcon-select-label"
                            name="subconId"
                            value={formData.subconId}
                            onChange={handleChange}
                            label="Subcon yang Mengerjakan"
                            sx={{
                              minHeight: '56px',
                              '& .MuiSelect-select': {
                                padding: '16px 14px',
                                fontSize: '1rem'
                              },
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#ccc'
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#3f51b5'
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#3f51b5'
                              }
                            }}
                          >
                            {installationTeamsLoading && (
                              <MenuItem value="">
                                <em>Memuat data subcon...</em>
                              </MenuItem>
                            )}
                            {!installationTeamsLoading && installationTeams.length === 0 && (
                              <MenuItem value="" disabled>
                                <em>Belum ada subcon aktif</em>
                              </MenuItem>
                            )}
                            {!installationTeamsLoading && installationTeams.map((team) => (
                              <MenuItem key={team.id} value={team.id.toString()}>
                                {team.name}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            {installationTeamsLoading
                              ? 'Mengambil data subcon...'
                              : 'Wajib pilih subcon yang mengerjakan progres ini.'}
                          </FormHelperText>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          required
                          label="Volume yang Telah Dikerjakan"
                          name="volume"
                          type="number"
                          value={formData.volume}
                          onChange={handleChange}
                          disabled={!formData.taskId || isBusy || isReadOnly || isTaskDone || totalVolume <= 0}
                          error={Boolean(volumeError)}
                          sx={{
                            '& .MuiInputLabel-root': {
                              fontSize: '1rem',
                              backgroundColor: 'white',
                              px: 1,
                              '&.Mui-focused': {
                                color: '#3f51b5'
                              }
                            },
                            '& .MuiOutlinedInput-root': {
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#3f51b5',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#3f51b5',
                              }
                            }
                          }}
                          InputProps={{
                            endAdornment: selectedTask && (
                              <InputAdornment position="end">
                                {selectedTask.unit}
                              </InputAdornment>
                            )
                          }}
                          inputProps={{ step: '0.01', min: 0 }}
                          helperText={volumeError || (selectedTask
                            ? `Sisa: ${formatNumber(remainingVolume)} ${selectedTask.unit || ''} · Maks 2 desimal`
                            : '')}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <FormControl
                          fullWidth
                          required
                          disabled={!formData.taskId || isBusy || isReadOnly}
                        >
                          <InputLabel
                            id="note-category-label"
                            sx={{
                              fontSize: '1rem',
                              backgroundColor: 'white',
                              px: 1,
                              '&.Mui-focused': {
                                color: '#3f51b5'
                              }
                            }}
                          >
                            Kategori Keterangan
                          </InputLabel>
                          <Select
                            labelId="note-category-label"
                            name="noteCategory"
                            value={formData.noteCategory}
                            onChange={handleChange}
                            label="Kategori Keterangan"
                            sx={{
                              minHeight: '56px',
                              '& .MuiSelect-select': {
                                padding: '16px 14px',
                                fontSize: '1rem'
                              },
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#ccc'
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#3f51b5'
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#3f51b5'
                              }
                            }}
                          >
                            {NOTE_CATEGORY_OPTIONS.map(option => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>Wajib pilih kategori keterangan.</FormHelperText>
                        </FormControl>
                      </Grid>

                      {formData.noteCategory === 'lainnya' && (
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="KETERANGAN TAMBAHAN"
                            name="notes"
                            multiline
                            rows={3}
                            value={formData.notes}
                            onChange={handleChange}
                            disabled={!formData.taskId || isBusy || isReadOnly}
                            required={isNotesRequired}
                            error={isNotesRequired && !formData.notes.trim()}
                            placeholder="Jelaskan alasan atau detail khusus terkait progres ini."
                            helperText="Wajib diisi jika kategori Lainnya dipilih."
                            sx={{
                              '& .MuiInputLabel-root': {
                                fontSize: '1rem',
                                backgroundColor: 'white',
                                px: 1,
                                '&.Mui-focused': {
                                  color: '#3f51b5'
                                }
                              },
                              '& .MuiOutlinedInput-root': {
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#3f51b5',
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#3f51b5',
                                }
                              }
                            }}
                          />
                        </Grid>
                      )}

                      <Grid item xs={12}>
                        {existingAttachments.length > 0 && (
                          <Alert severity="info" sx={{ mb: 2 }}>
                            Lampiran tersimpan:
                            <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                              {existingAttachments.map((attachment) => (
                                <li key={attachment.id} style={{ marginBottom: 6 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {getAttachmentIcon(attachment.fileType)}
                                    <a
                                      href={getAttachmentUrl(attachment.fileUrl)}
                                      target="_blank"
                                      rel="noreferrer"
                                      style={{ color: '#1e88e5', textDecoration: 'none' }}
                                    >
                                      {attachment.fileName || 'Lampiran'}
                                    </a>
                                    {!isReadOnly && (
                                      <Button
                                        size="small"
                                        color="error"
                                        onClick={() => handleRemoveExistingAttachment(attachment.id)}
                                        sx={{ minWidth: 'auto', p: '2px' }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </Button>
                                    )}
                                  </Box>
                                </li>
                              ))}
                            </ul>
                          </Alert>
                        )}
                        <Button
                          component="label"
                          variant="outlined"
                          startIcon={<FileUploadIcon />}
                          sx={{ mt: 1 }}
                          disabled={!formData.taskId || isBusy || isReadOnly || isTaskDone || totalVolume <= 0}
                          fullWidth
                        >
                          UNGGAH BUKTI PEKERJAAN (FOTO/PDF/DOKUMEN)
                          <input
                            id="attachment-input"
                            type="file"
                            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                            hidden
                            multiple
                            onChange={handleFileChange}
                          />
                        </Button>
                        {formData.attachments && formData.attachments.length > 0 && (
                          <Alert severity="success" sx={{ mt: 1 }}>
                            {formData.attachments.length} file dipilih:
                            <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                              {formData.attachments.map((attachment, index) => (
                                <li key={index}>
                                  {attachment.file.name} ({(attachment.file.size / 1024).toFixed(1)} KB)
                                  <Button
                                    size="small"
                                    color="error"
                                    onClick={() => handleRemoveFile(index)}
                                    sx={{ ml: 1, minWidth: 'auto', p: '2px' }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </Button>
                                </li>
                              ))}
                            </ul>
                          </Alert>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            
            {/* Submit Button */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={!canSubmit}
                sx={{ 
                  minWidth: 200, 
                  py: 1.5,
                  fontSize: '1rem',
                  boxShadow: 3,
                  '&:hover': {
                    boxShadow: 5
                  }
                }}
              >
                {submitting ? <CircularProgress size={24} /> : (isEditing ? 'Kirim Ulang Pembaharuan' : 'Kirim Laporan Progress')}
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>
      
      {/* Error and Success Snackbars */}
      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      
      <Snackbar open={!!success} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UpdateProgress;
